import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ecommerceApi, inventoryApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/** FR-ECOM-01 + FR-ECOM-02 — Storefronts, catalog, collections */
export function StorefrontsPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['storefronts'],
    queryFn: () => ecommerceApi.listStorefronts(),
  });

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Online store"
        title="Storefronts"
        subtitle="Manage your online storefronts — catalogs, collections, and content pages."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New storefront
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <div className="card overflow-hidden">
          {q.isLoading ? <LoadingState rows={4} /> : q.error ? <ErrorState error={q.error} /> :
           !q.data?.length ? (
            <EmptyState
              message="No storefronts"
              hint="Create one to start selling online."
              action={
                <button className="btn-primary" onClick={() => setShowCreate(true)}>
                  + New storefront
                </button>
              }
            />
          ) : (
            <div className="divide-y divide-ink-100">
              {(q.data ?? []).map((sf: any) => (
                <div
                  key={sf.id}
                  onClick={() => setSelected(sf.id)}
                  className={`p-4 cursor-pointer transition-colors ${
                    selected === sf.id ? 'bg-canvas-subtle' : 'hover:bg-canvas-subtle/50'
                  }`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-ink-900 truncate">{sf.name}</div>
                      <div className="text-2xs text-ink-500 font-mono mt-0.5">{sf.code}</div>
                      {sf.domain && <div className="text-2xs text-ink-500 truncate mt-0.5">{sf.domain}</div>}
                    </div>
                    <StorefrontStatusBadge status={sf.status} />
                  </div>
                  <div className="flex gap-2 text-2xs text-ink-500 mt-2 font-mono">
                    <span className="tabular-nums">{sf.catalog_item_count ?? 0} items</span>
                    <span className="text-ink-300">·</span>
                    <span className="tabular-nums">{sf.collection_count} collections</span>
                    <span className="text-ink-300">·</span>
                    <span className="tabular-nums">{sf.page_count} pages</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          {!selected ? (
            <div className="card p-8">
              <EmptyState
                message="Select a storefront"
                hint="Click a storefront on the left to manage its catalog, collections, and pages."
              />
            </div>
          ) : <StorefrontDetail storefrontId={selected}
                onChanged={() => qc.invalidateQueries({ queryKey: ['storefronts'] })} />
          }
        </div>
      </div>

      {showCreate && <CreateStorefrontModal onClose={() => setShowCreate(false)}
        onCreated={(id) => { qc.invalidateQueries({ queryKey: ['storefronts'] }); setShowCreate(false); setSelected(id); }} />}
    </div>
  );
}

function StorefrontStatusBadge({ status }: { status: string }) {
  const cls = {
    active: 'bg-sage-50 text-sage-700 ring-1 ring-inset ring-sage-200',
    draft: 'bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200',
    disabled: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200';
  return <span className={`badge ${cls} text-2xs`}>{status}</span>;
}

type DetailTab = 'settings' | 'catalog' | 'collections' | 'pages';

function StorefrontDetail({ storefrontId, onChanged }:
    { storefrontId: string; onChanged: () => void }) {
  const [tab, setTab] = useState<DetailTab>('settings');

  const sfQ = useQuery({
    queryKey: ['storefront', storefrontId],
    queryFn: () => ecommerceApi.getStorefront(storefrontId),
  });

  if (sfQ.isLoading) return <LoadingState />;
  if (sfQ.error) return <ErrorState error={sfQ.error} />;

  return (
    <div>
      <div className="card p-5 mb-4 flex justify-between items-start gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{sfQ.data.code}</div>
          <div className="font-display font-bold text-2xl text-ink-900 tracking-tight">{sfQ.data.name}</div>
          {sfQ.data.domain && (
            <div className="text-sm text-ink-600 mt-0.5 font-mono">{sfQ.data.domain}</div>
          )}
          {sfQ.data.published_catalog_at && (
            <div className="text-xs text-sage-700 mt-2">
              Catalog published {new Date(sfQ.data.published_catalog_at).toLocaleDateString()}
              <span className="text-sage-600 mx-1">·</span>
              <span className="font-mono tabular-nums">{sfQ.data.catalog_item_count} items</span>
            </div>
          )}
        </div>
        <StorefrontStatusBadge status={sfQ.data.status} />
      </div>

      <div className="card p-1.5 mb-4 inline-flex gap-1">
        {([
          ['settings', 'Settings'],
          ['catalog', 'Catalog'],
          ['collections', 'Collections'],
          ['pages', 'Pages'],
        ] as [DetailTab, string][]).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-pill transition-all ${
              tab === k
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'settings' && <SettingsTab storefront={sfQ.data}
        onSaved={() => { sfQ.refetch(); onChanged(); }} />}
      {tab === 'catalog' && <CatalogTab storefrontId={storefrontId}
        onPublished={() => { sfQ.refetch(); onChanged(); }} />}
      {tab === 'collections' && <CollectionsTab storefrontId={storefrontId} />}
      {tab === 'pages' && <PagesTab storefrontId={storefrontId} />}
    </div>
  );
}

function SettingsTab({ storefront, onSaved }: { storefront: any; onSaved: () => void }) {
  const [name, setName] = useState(storefront.name ?? '');
  const [domain, setDomain] = useState(storefront.domain ?? '');
  const [status, setStatus] = useState(storefront.status ?? 'draft');
  const [metaTitle, setMetaTitle] = useState(storefront.meta_title ?? '');
  const [metaDescription, setMetaDescription] = useState(storefront.meta_description ?? '');
  const [logoUrl, setLogoUrl] = useState(storefront.logo_url ?? '');
  const [primaryColor, setPrimaryColor] = useState(storefront.primary_color ?? '');

  const mut = useMutation({
    mutationFn: () => ecommerceApi.updateStorefront(storefront.id, {
      name, domain: domain || undefined, status,
      metaTitle: metaTitle || undefined,
      metaDescription: metaDescription || undefined,
      logoUrl: logoUrl || undefined,
      primaryColor: primaryColor || undefined,
    }),
    onSuccess: () => { toast.success('Saved'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="card p-4">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
        <div>
          <label className="label">Domain</label>
          <input className="input" value={domain} onChange={(e) => setDomain(e.target.value)}
            placeholder="shop.example.com" />
        </div>
        <div>
          <label className="label">Logo URL</label>
          <input className="input" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="label">Meta title (SEO)</label>
          <input className="input" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="label">Meta description (SEO)</label>
          <textarea className="input" rows={2} value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value)} />
        </div>
        <div>
          <label className="label">Primary color</label>
          <input className="input" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)}
            placeholder="#4f46e5" />
        </div>
      </div>
      <div className="flex justify-end">
        <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

function CatalogTab({ storefrontId, onPublished }:
    { storefrontId: string; onPublished: () => void }) {
  const qc = useQueryClient();
  const [showPublish, setShowPublish] = useState(false);
  const [search, setSearch] = useState('');

  const q = useQuery({
    queryKey: ['storefront-catalog', storefrontId, search],
    queryFn: () => ecommerceApi.getCatalog(storefrontId, { search: search || undefined, limit: 200 }),
  });

  return (
    <div>
      <div className="card p-3 mb-3 flex gap-3 items-end">
        <div className="flex-1">
          <label className="label">Search</label>
          <input className="input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="name or SKU" />
        </div>
        <button className="btn-primary" onClick={() => setShowPublish(true)}>Publish catalog</button>
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="card overflow-x-auto">
          <table className="table">
            <thead><tr><th>SKU</th><th>Product</th><th className="text-right">Price</th><th>Stock</th><th>Visibility</th><th>Last sync</th></tr></thead>
            <tbody>
              {!q.data?.length ? (
                <tr><td colSpan={6}><EmptyState message="Catalog empty. Click 'Publish catalog' to add products." /></td></tr>
              ) : (q.data ?? []).map((c: any) => (
                <tr key={c.id}>
                  <td className="font-mono text-xs">{c.sku}</td>
                  <td className="text-xs">{c.display_name}</td>
                  <td className="text-right font-mono">{fmtCurrency(parseFloat(c.display_price))}</td>
                  <td>
                    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${
                      c.in_stock ? 'bg-sage-50 text-sage-700 ring-sage-200' : 'bg-red-50 text-red-700 ring-red-200'
                    }`}>
                      {c.in_stock ? 'in stock' : 'out'}
                    </span>
                  </td>
                  <td>
                    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${
                      c.visibility === 'visible'  ? 'bg-sage-50 text-sage-700 ring-sage-200' :
                      c.visibility === 'featured' ? 'bg-blue-50 text-blue-700 ring-blue-200' :
                      'bg-canvas-subtle text-ink-500 ring-ink-200'
                    }`}>{c.visibility}</span>
                  </td>
                  <td className="text-2xs text-ink-500 font-mono tabular-nums">
                    {new Date(c.last_synced_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPublish && <PublishCatalogModal storefrontId={storefrontId}
        onClose={() => setShowPublish(false)}
        onPublished={() => { qc.invalidateQueries({ queryKey: ['storefront-catalog'] }); setShowPublish(false); onPublished(); }} />}
    </div>
  );
}

function PublishCatalogModal({ storefrontId, onClose, onPublished }:
    { storefrontId: string; onClose: () => void; onPublished: () => void }) {
  const catsQ = useQuery({ queryKey: ['categories'], queryFn: () => inventoryApi.listCategories() });
  const [categoryIds, setCategoryIds] = useState<Set<string>>(new Set());
  const [overwrite, setOverwrite] = useState(false);
  const [removeMissing, setRemoveMissing] = useState(false);

  const mut = useMutation({
    mutationFn: () => ecommerceApi.publishCatalog(storefrontId, {
      categoryIds: categoryIds.size > 0 ? Array.from(categoryIds) : undefined,
      overwrite, removeMissing,
    }),
    onSuccess: (d) => {
      toast.success(`Processed ${d.processed} · ${d.inserted} new · ${d.updated} updated${d.removed > 0 ? ` · ${d.removed} hidden` : ''}`, { duration: 6000 });
      onPublished();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const toggle = (id: string) => {
    const s = new Set(categoryIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setCategoryIds(s);
  };

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Commerce · Catalog</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Publish catalog</h2>
        <p className="text-sm text-ink-500 mt-1 leading-relaxed">
          Walks active products and upserts them into this storefront's catalog.
          Preserves any per-storefront price overrides by default — use Overwrite to reset them.
        </p>
      </div>

      <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0 space-y-4">
        <div>
          <label className="label">Filter by categories <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <div className="border border-ink-100 rounded-xl max-h-48 overflow-y-auto">
            {catsQ.isLoading ? <LoadingState rows={3} /> : !catsQ.data?.length ? (
              <div className="text-xs text-ink-400 p-4 text-center">No categories configured</div>
            ) : (catsQ.data ?? []).map((c: any) => (
              <label key={c.id}
                className="flex items-center gap-2.5 px-3 py-2 hover:bg-canvas-subtle text-sm cursor-pointer border-b border-ink-100 last:border-b-0 transition-colors">
                <input type="checkbox" checked={categoryIds.has(c.id)} onChange={() => toggle(c.id)} />
                <span className="text-ink-900">{c.name}</span>
              </label>
            ))}
          </div>
          <div className="text-2xs text-ink-500 mt-1">
            {categoryIds.size === 0 ? 'All active products will be published.' : `${categoryIds.size} categor${categoryIds.size === 1 ? 'y' : 'ies'} selected.`}
          </div>
        </div>

        <div className="space-y-0">
          <label className="flex items-start gap-2.5 py-3 border-b border-ink-100 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
            <div>
              <div className="text-sm font-medium text-ink-900">Overwrite existing entries</div>
              <div className="text-2xs text-ink-500 mt-0.5">Resets any per-storefront price and name overrides to master values.</div>
            </div>
          </label>
          <label className="flex items-start gap-2.5 py-3 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={removeMissing} onChange={(e) => setRemoveMissing(e.target.checked)} />
            <div>
              <div className="text-sm font-medium text-ink-900">Hide products no longer matching</div>
              <div className="text-2xs text-ink-500 mt-0.5">Soft-deletes catalog entries for products that no longer match the category filter.</div>
            </div>
          </label>
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Publishing…' : 'Publish catalog'}
        </button>
      </div>
    </Modal>
  );
}

function CollectionsTab({ storefrontId }: { storefrontId: string }) {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const q = useQuery({
    queryKey: ['storefront-collections', storefrontId],
    queryFn: () => ecommerceApi.listCollections(storefrontId),
  });

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New collection</button>
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {!q.data?.length ? (
            <div className="col-span-full"><EmptyState message="No collections yet." /></div>
          ) : (q.data ?? []).map((c: any) => (
            <div key={c.id} className="card p-4">
              <div className="flex justify-between items-start mb-2 gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-2xs text-ink-500 mb-0.5">{c.code}</div>
                  <div className="font-semibold text-ink-900 truncate">{c.name}</div>
                  <div className="text-2xs text-ink-400 font-mono mt-0.5">/{c.slug}</div>
                </div>
                <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset flex-shrink-0 ${
                  c.collection_type === 'smart' ? 'bg-blue-50 text-blue-700 ring-blue-200' : 'bg-canvas-subtle text-ink-600 ring-ink-200'
                }`}>
                  {c.collection_type}
                </span>
              </div>
              {c.description && <div className="text-xs text-ink-600 line-clamp-2 mb-2 leading-relaxed">{c.description}</div>}
              <div className="text-xs text-ink-500">
                {c.collection_type === 'manual' ? `${c.manual_product_count} products` : 'rule-based'}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateCollectionModal storefrontId={storefrontId}
        onClose={() => setShowCreate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['storefront-collections'] }); setShowCreate(false); }} />}
    </div>
  );
}

function CreateStorefrontModal({ onClose, onCreated }:
    { onClose: () => void; onCreated: (id: string) => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [currencyCode, setCurrencyCode] = useState('USD');

  const mut = useMutation({
    mutationFn: () => ecommerceApi.createStorefront({
      code, name, domain: domain || undefined, currencyCode,
    }),
    onSuccess: (d) => { toast.success('Storefront created'); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Commerce · New</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New storefront</h2>
        <p className="text-sm text-ink-500 mt-1">
          Starts in draft. After creation, publish the catalog and flip status to active.
        </p>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-4">
        <div>
          <label className="label">Code <span className="text-danger">*</span></label>
          <input className="input font-mono" value={code} onChange={(e) => setCode(e.target.value)}
            placeholder="main-store" />
        </div>
        <div>
          <label className="label">Currency</label>
          <select className="input" value={currencyCode} onChange={(e) => setCurrencyCode(e.target.value)}>
            <option>USD</option><option>EUR</option><option>GBP</option>
            <option>JPY</option><option>CAD</option><option>AUD</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="OmniRetail Store" />
        </div>
        <div className="col-span-2">
          <label className="label">Domain <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input font-mono text-xs" value={domain} onChange={(e) => setDomain(e.target.value)}
            placeholder="shop.example.com" />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!code || !name || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create storefront'}
        </button>
      </div>
    </Modal>
  );
}

function CreateCollectionModal({ storefrontId, onClose, onCreated }:
    { storefrontId: string; onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [collectionType, setCollectionType] = useState<'manual' | 'smart'>('manual');
  const [priceMin, setPriceMin] = useState<string>('');
  const [priceMax, setPriceMax] = useState<string>('');

  const mut = useMutation({
    mutationFn: () => ecommerceApi.createCollection(storefrontId, {
      code, name, slug, description: description || undefined,
      collectionType,
      rules: collectionType === 'smart' ? {
        price_min: priceMin ? parseFloat(priceMin) : undefined,
        price_max: priceMax ? parseFloat(priceMax) : undefined,
      } : undefined,
    }),
    onSuccess: () => { toast.success('Collection created'); onCreated(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Commerce · Collection</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New collection</h2>
        <p className="text-sm text-ink-500 mt-1">
          Manual collections are curated by hand. Smart collections auto-populate by price rules.
        </p>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-4">
        <div>
          <label className="label">Code <span className="text-danger">*</span></label>
          <input className="input font-mono" value={code} onChange={(e) => setCode(e.target.value)}
            placeholder="SUMMER_2026" />
        </div>
        <div>
          <label className="label">Type</label>
          <select className="input" value={collectionType}
            onChange={(e) => setCollectionType(e.target.value as any)}>
            <option value="manual">Manual</option>
            <option value="smart">Smart (rules)</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => {
            setName(e.target.value);
            if (!slug) setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
          }} />
        </div>
        <div className="col-span-2">
          <label className="label">Slug <span className="text-danger">*</span></label>
          <input className="input font-mono text-xs" value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} />
        </div>
        <div className="col-span-2">
          <label className="label">Description <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={2} value={description}
            onChange={(e) => setDescription(e.target.value)} />
        </div>
        {collectionType === 'smart' && (
          <>
            <div>
              <label className="label">Price min <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
              <input type="number" min={0} className="input tabular-nums" value={priceMin}
                onChange={(e) => setPriceMin(e.target.value)} />
            </div>
            <div>
              <label className="label">Price max <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
              <input type="number" min={0} className="input tabular-nums" value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)} />
            </div>
          </>
        )}
        {collectionType === 'manual' && (
          <div className="col-span-2 text-2xs text-ink-500 leading-relaxed">
            Manual collection starts empty. Add products to it after creation.
          </div>
        )}
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!code || !name || !slug || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create collection'}
        </button>
      </div>
    </Modal>
  );
}

function PagesTab({ storefrontId }: { storefrontId: string }) {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingPage, setDeletingPage] = useState<any | null>(null);

  const q = useQuery({
    queryKey: ['storefront-pages', storefrontId],
    queryFn: () => ecommerceApi.listPages(storefrontId),
  });

  const publishMut = useMutation({
    mutationFn: (id: string) => ecommerceApi.publishPage(storefrontId, id),
    onSuccess: () => { toast.success('Published'); qc.invalidateQueries({ queryKey: ['storefront-pages'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const unpublishMut = useMutation({
    mutationFn: (id: string) => ecommerceApi.unpublishPage(id),
    onSuccess: () => { toast.success('Unpublished'); qc.invalidateQueries({ queryKey: ['storefront-pages'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => ecommerceApi.deletePage(id),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['storefront-pages'] });
      setDeletingPage(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New page</button>
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="card overflow-x-auto">
          <table className="table">
            <thead><tr><th>Slug</th><th>Title</th><th>Type</th><th className="text-right">Blocks</th><th>Status</th><th>Nav</th><th></th></tr></thead>
            <tbody>
              {!q.data?.length ? (
                <tr><td colSpan={7}><EmptyState message="No pages yet." /></td></tr>
              ) : (q.data ?? []).map((p: any) => (
                <tr key={p.id}>
                  <td className="font-mono text-xs text-ink-700">/{p.slug}</td>
                  <td className="font-medium text-ink-900 text-xs">{p.title}</td>
                  <td className="text-xs text-ink-600 capitalize">{p.page_type}</td>
                  <td className="text-right font-mono tabular-nums text-xs text-ink-600">{p.block_count}</td>
                  <td>
                    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${
                      p.status === 'published' ? 'bg-sage-50 text-sage-700 ring-sage-200' : 'bg-canvas-subtle text-ink-600 ring-ink-200'
                    }`}>{p.status}</span>
                  </td>
                  <td>
                    {p.show_in_nav && (
                      <span className="text-2xs font-semibold text-sage-700">✓</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-1 justify-end">
                      <button
                        className="text-xs font-semibold text-ink-600 hover:text-ink-900 hover:bg-canvas-subtle px-2 py-1 rounded transition-colors"
                        onClick={() => setEditingId(p.id)}
                      >Edit</button>
                      {p.status === 'draft' && p.block_count > 0 && (
                        <button
                          className="text-xs font-semibold text-sage-700 hover:bg-sage-50 px-2 py-1 rounded transition-colors"
                          disabled={publishMut.isPending}
                          onClick={() => publishMut.mutate(p.id)}
                        >Publish</button>
                      )}
                      {p.status === 'published' && (
                        <button
                          className="text-xs font-medium text-ink-500 hover:bg-canvas-subtle px-2 py-1 rounded transition-colors"
                          onClick={() => unpublishMut.mutate(p.id)}
                        >Unpublish</button>
                      )}
                      <button
                        className="text-xs font-semibold text-danger hover:bg-red-50 px-2 py-1 rounded transition-colors"
                        onClick={() => setDeletingPage(p)}
                      >Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreatePageModal storefrontId={storefrontId}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => { qc.invalidateQueries({ queryKey: ['storefront-pages'] }); setShowCreate(false); setEditingId(id); }} />}
      {editingId && <PageEditorModal pageId={editingId}
        onClose={() => setEditingId(null)}
        onSaved={() => { qc.invalidateQueries({ queryKey: ['storefront-pages'] }); }} />}
      {deletingPage && (
        <DeletePageDialog
          page={deletingPage}
          loading={deleteMut.isPending}
          onClose={() => setDeletingPage(null)}
          onConfirm={() => deleteMut.mutate(deletingPage.id)}
        />
      )}
    </div>
  );
}

function DeletePageDialog({
  page, loading, onClose, onConfirm,
}: { page: any; loading: boolean; onClose: () => void; onConfirm: () => void }) {
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Commerce · Delete page</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Delete this page?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Delete <span className="font-mono font-semibold text-ink-900">/{page.slug}</span>
          {' '}— <span className="text-ink-700">{page.title}</span>?
          All blocks will be permanently removed. This cannot be undone.
        </p>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={loading}
          onClick={onConfirm}
        >
          {loading ? 'Deleting…' : 'Delete page'}
        </button>
      </div>
    </Modal>
  );
}

function CreatePageModal({ storefrontId, onClose, onCreated }:
    { storefrontId: string; onClose: () => void; onCreated: (id: string) => void }) {
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [pageType, setPageType] = useState<'content'|'landing'|'about'|'contact'|'policy'|'blog'>('content');
  const [showInNav, setShowInNav] = useState(false);

  const mut = useMutation({
    mutationFn: () => ecommerceApi.createPage(storefrontId, {
      slug, title, pageType, showInNav, blocks: [],
    }),
    onSuccess: (d) => { toast.success('Page created — open to add blocks'); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Commerce · Page</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New page</h2>
        <p className="text-sm text-ink-500 mt-1">
          Creates an empty draft. Open the editor to add blocks.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Title <span className="text-danger">*</span></label>
          <input className="input" value={title} onChange={(e) => {
            setTitle(e.target.value);
            if (!slug) setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
          }} placeholder="About Us" />
        </div>
        <div>
          <label className="label">Slug <span className="text-danger">*</span></label>
          <input className="input font-mono text-xs" value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="about-us" />
        </div>
        <div>
          <label className="label">Page type</label>
          <select className="input" value={pageType}
            onChange={(e) => setPageType(e.target.value as any)}>
            <option value="content">Content</option>
            <option value="landing">Landing</option>
            <option value="about">About</option>
            <option value="contact">Contact</option>
            <option value="policy">Policy</option>
            <option value="blog">Blog</option>
          </select>
        </div>
        <label className="flex items-center gap-2.5 text-sm text-ink-700 cursor-pointer">
          <input type="checkbox" checked={showInNav} onChange={(e) => setShowInNav(e.target.checked)} />
          Show in navigation
        </label>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!slug || !title || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create page'}
        </button>
      </div>
    </Modal>
  );
}

function PageEditorModal({ pageId, onClose, onSaved }:
    { pageId: string; onClose: () => void; onSaved: () => void }) {
  const q = useQuery({
    queryKey: ['page', pageId],
    queryFn: () => ecommerceApi.getPage(pageId),
  });

  const [title, setTitle] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [blocks, setBlocks] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Load once
  if (q.data && !loaded) {
    setTitle(q.data.title ?? '');
    setMetaTitle(q.data.meta_title ?? '');
    setMetaDescription(q.data.meta_description ?? '');
    setHeroImageUrl(q.data.hero_image_url ?? '');
    setBlocks(q.data.blocks ?? []);
    setLoaded(true);
  }

  const mut = useMutation({
    mutationFn: () => ecommerceApi.updatePage(pageId, {
      title,
      metaTitle: metaTitle || undefined,
      metaDescription: metaDescription || undefined,
      heroImageUrl: heroImageUrl || undefined,
      blocks,
    }),
    onSuccess: () => { toast.success('Saved'); onSaved(); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const addBlock = (type: string) => {
    const defaults: Record<string, any> = {
      hero: { type: 'hero', heading: 'New heading', subheading: '' },
      text: { type: 'text', content: 'New text content', format: 'plain' },
      image: { type: 'image', url: 'https://', alt: '' },
      products: { type: 'products', limit: 8, product_ids: [] },
      cta: { type: 'cta', text: 'Click me', url: '/', style: 'primary' },
    };
    setBlocks([...blocks, defaults[type]]);
  };

  const updateBlock = (i: number, patch: any) => {
    const next = [...blocks];
    next[i] = { ...next[i], ...patch };
    setBlocks(next);
  };
  const moveBlock = (i: number, dir: number) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    setBlocks(next);
  };
  const removeBlock = (i: number) => {
    setBlocks(blocks.filter((_, idx) => idx !== i));
  };

  if (q.isLoading) return null;
  if (q.error) return null;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0 flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow mb-1">Commerce · Page editor</div>
          <h2 className="text-xl font-bold font-display text-ink-900">{q.data.title}</h2>
          <div className="text-xs font-mono text-ink-500 mt-0.5">/{q.data.slug}</div>
        </div>
        <button
          className="w-8 h-8 flex items-center justify-center text-ink-400 hover:text-ink-900 hover:bg-canvas-subtle rounded-md transition-colors flex-shrink-0 mt-1"
          onClick={onClose} aria-label="Close"
        >✕</button>
      </div>

      <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
        {/* Meta */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div className="col-span-2">
            <label className="label">Title</label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="label">Hero image URL <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
            <input className="input font-mono text-xs" value={heroImageUrl}
              onChange={(e) => setHeroImageUrl(e.target.value)} />
          </div>
          <div>
            <label className="label">Meta title <span className="font-normal text-ink-400 normal-case tracking-normal">(SEO)</span></label>
            <input className="input" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} />
          </div>
          <div>
            <label className="label">Meta description <span className="font-normal text-ink-400 normal-case tracking-normal">(SEO)</span></label>
            <textarea className="input" rows={2} value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)} />
          </div>
        </div>

        {/* Blocks */}
        <div className="border-t border-ink-100 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow text-ink-500">Blocks ({blocks.length})</div>
            <div className="flex gap-1">
              {['hero', 'text', 'image', 'products', 'cta'].map((t) => (
                <button key={t} className="btn-secondary text-xs py-1"
                  onClick={() => addBlock(t)}>+ {t}</button>
              ))}
            </div>
          </div>

          {blocks.length === 0 ? (
            <div className="text-xs text-ink-400 text-center py-8 bg-canvas-subtle rounded-xl">
              No blocks yet. Click a button above to add your first block.
            </div>
          ) : (
            <div className="space-y-2">
              {blocks.map((b, i) => (
                <BlockEditor key={i} block={b} index={i}
                  onChange={(patch) => updateBlock(i, patch)}
                  onMove={(dir) => moveBlock(i, dir)}
                  onRemove={() => removeBlock(i)}
                  isFirst={i === 0} isLast={i === blocks.length - 1} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Saving…' : 'Save page'}
        </button>
      </div>
    </Modal>
  );
}

function BlockEditor({ block, index, onChange, onMove, onRemove, isFirst, isLast }: {
  block: any;
  index: number;
  onChange: (patch: Record<string, any>) => void;
  onMove: (dir: number) => void;
  onRemove: () => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  return (
    <div className="border border-ink-100 rounded-xl p-3 bg-canvas-elevated">
      <div className="flex justify-between items-center mb-3">
        <span className="font-mono text-xs font-semibold bg-canvas-subtle text-ink-700 px-2 py-0.5 rounded-lg">
          {index + 1}. {block.type}
        </span>
        <div className="flex items-center gap-1">
          <button
            className="w-6 h-6 flex items-center justify-center text-xs text-ink-500 hover:text-ink-900 disabled:opacity-30 hover:bg-canvas-subtle rounded transition-colors"
            disabled={isFirst} onClick={() => onMove(-1)}>↑</button>
          <button
            className="w-6 h-6 flex items-center justify-center text-xs text-ink-500 hover:text-ink-900 disabled:opacity-30 hover:bg-canvas-subtle rounded transition-colors"
            disabled={isLast} onClick={() => onMove(1)}>↓</button>
          <button
            className="w-6 h-6 flex items-center justify-center text-xs text-danger hover:bg-red-50 rounded transition-colors"
            onClick={onRemove}>✕</button>
        </div>
      </div>
      {block.type === 'hero' && (
        <div className="grid grid-cols-1 gap-2">
          <input className="input text-xs" placeholder="Heading" value={block.heading ?? ''}
            onChange={(e) => onChange({ heading: e.target.value })} />
          <input className="input text-xs" placeholder="Subheading" value={block.subheading ?? ''}
            onChange={(e) => onChange({ subheading: e.target.value })} />
          <input className="input text-xs font-mono" placeholder="Image URL" value={block.image_url ?? ''}
            onChange={(e) => onChange({ image_url: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <input className="input text-xs" placeholder="CTA text" value={block.cta_text ?? ''}
              onChange={(e) => onChange({ cta_text: e.target.value })} />
            <input className="input text-xs font-mono" placeholder="CTA URL" value={block.cta_url ?? ''}
              onChange={(e) => onChange({ cta_url: e.target.value })} />
          </div>
        </div>
      )}
      {block.type === 'text' && (
        <div className="grid grid-cols-1 gap-2">
          <select className="input text-xs" value={block.format ?? 'plain'}
            onChange={(e) => onChange({ format: e.target.value })}>
            <option value="plain">Plain text</option>
            <option value="markdown">Markdown</option>
            <option value="html">HTML</option>
          </select>
          <textarea className="input text-xs font-mono" rows={4} value={block.content ?? ''}
            onChange={(e) => onChange({ content: e.target.value })} />
        </div>
      )}
      {block.type === 'image' && (
        <div className="grid grid-cols-1 gap-2">
          <input className="input text-xs font-mono" placeholder="Image URL" value={block.url ?? ''}
            onChange={(e) => onChange({ url: e.target.value })} />
          <input className="input text-xs" placeholder="Alt text" value={block.alt ?? ''}
            onChange={(e) => onChange({ alt: e.target.value })} />
          <input className="input text-xs" placeholder="Caption" value={block.caption ?? ''}
            onChange={(e) => onChange({ caption: e.target.value })} />
        </div>
      )}
      {block.type === 'products' && (
        <div className="grid grid-cols-1 gap-2">
          <input className="input text-xs" placeholder="Heading (optional)" value={block.heading ?? ''}
            onChange={(e) => onChange({ heading: e.target.value })} />
          <input className="input text-xs font-mono" placeholder="collection_id (UUID)"
            value={block.collection_id ?? ''}
            onChange={(e) => onChange({ collection_id: e.target.value || undefined })} />
          <input type="number" className="input text-xs tabular-nums" placeholder="Limit" value={block.limit ?? 8}
            onChange={(e) => onChange({ limit: parseInt(e.target.value) || 8 })} />
        </div>
      )}
      {block.type === 'cta' && (
        <div className="grid grid-cols-2 gap-2">
          <input className="input text-xs" placeholder="Button text" value={block.text ?? ''}
            onChange={(e) => onChange({ text: e.target.value })} />
          <input className="input text-xs font-mono" placeholder="URL" value={block.url ?? ''}
            onChange={(e) => onChange({ url: e.target.value })} />
          <select className="input text-xs col-span-2" value={block.style ?? 'primary'}
            onChange={(e) => onChange({ style: e.target.value })}>
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="outline">Outline</option>
          </select>
        </div>
      )}
    </div>
  );
}
